import type { OrdersFacets } from "@/api/custom_types";
import { toFacetFilterOptions } from "@/components";
import { useAutoApplyFilters } from "@/hooks/useAutoApplyFilters";
import type { FacetCounts } from "@/hooks/useFacetFilters";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetAllBuyers, useGetAllSellers } from "@/services";
import { useOrderStatusTranslator } from "@/utils/translations";
import {
	type FieldConfig,
	type FilterConfig,
	GenericFilters,
} from "@st-marketplace/ui";
import { useMemo } from "react";
import type {
	OrderFilterFieldKey,
	OrdersFilterValues,
	OrdersSchemaType,
} from "./types";

const FIELDS_BY_SCHEMA: Record<OrdersSchemaType, OrderFilterFieldKey[]> = {
	seller: ["email", "status", "buyer"],
	buyer: ["email", "status", "seller"],
	admin: ["email", "status", "buyer", "seller"],
};

export interface OrdersTableFilters {
	email: string;
	status: string[];
	buyer: string;
	seller: string;
}

function toTableFilters(values?: OrdersFilterValues): OrdersTableFilters {
	return {
		email: values?.email ?? "",
		status: values?.status ? [...values.status] : [],
		buyer: values?.buyer ?? "",
		seller: values?.seller ?? "",
	};
}

type Props = {
	schemaType: OrdersSchemaType;
	facetsCounts: FacetCounts<OrdersFacets>;
	defaultValues?: OrdersFilterValues;
	onSearch: (values: OrdersFilterValues) => void;
};

export const OrdersFilterForm: React.FC<Props> = ({
	schemaType,
	facetsCounts,
	defaultValues,
	onSearch,
}) => {
	const { t } = useTranslation();
	const translateStatus = useOrderStatusTranslator(t);

	const fields = FIELDS_BY_SCHEMA[schemaType];

	const { filters, onFiltersChange, clearFilters } =
		useAutoApplyFilters<OrdersTableFilters>({
			initialFilters: toTableFilters(defaultValues),
			onApply: (next) => onSearch(next as OrdersFilterValues),
			textFilterKeys: ["email"],
		});

	const needsBuyers = fields.includes("buyer");
	const needsSellers = fields.includes("seller");

	const { data: buyers } = useGetAllBuyers({}, { enabled: needsBuyers });
	const { data: sellers } = useGetAllSellers({}, { enabled: needsSellers });

	const statusOptions = useMemo(
		() =>
			toFacetFilterOptions(
				facetsCounts.statuses,
				(status) => translateStatus(status).text as string,
			),
		[facetsCounts.statuses, translateStatus],
	);

	const buyerOptions = useMemo(
		() =>
			(buyers ?? []).map((buyer: { id: string | number; name: string }) => ({
				value: String(buyer.id),
				label: buyer.name,
			})),
		[buyers],
	);

	const sellerOptions = useMemo(
		() =>
			(sellers ?? []).map((seller: { id: string | number; name: string }) => ({
				value: String(seller.id),
				label: seller.name,
			})),
		[sellers],
	);

	const config = useMemo<FilterConfig<OrdersTableFilters>>(() => {
		const filterFields: FieldConfig<OrdersTableFilters>[] = [];

		if (fields.includes("email")) {
			filterFields.push({
				key: "email",
				type: "search",
				placeholder: t("order.filterEmail"),
			});
		}

		if (fields.includes("status")) {
			filterFields.push({
				key: "status",
				type: "multi-select",
				placeholder: t("order.filterStatus"),
				options: statusOptions,
				searchable: true,
			});
		}

		if (fields.includes("buyer")) {
			filterFields.push({
				key: "buyer",
				type: "select",
				placeholder: t("order.filterBuyer"),
				options: buyerOptions,
				searchable: true,
			});
		}

		if (fields.includes("seller")) {
			filterFields.push({
				key: "seller",
				type: "select",
				placeholder: t("order.filterSeller"),
				options: sellerOptions,
				searchable: true,
			});
		}

		return {
			fields: filterFields,
			searchDebounceMs: 0,
			showClearButton: true,
		};
	}, [fields, t, statusOptions, buyerOptions, sellerOptions]);

	const handleClearFilters = () => {
		clearFilters(toTableFilters());
	};

	return (
		<GenericFilters
			config={config}
			filters={filters}
			onFiltersChange={onFiltersChange}
			onClearFilters={handleClearFilters}
		/>
	);
};
